/**
 * Pure helpers for the `image_upload` field value union.
 *
 * Reads tolerate three on-disk shapes:
 *
 *   1. Legacy bare string (single image, pre-PR #295).
 *   2. Array of bare strings (PR #295 multi-image, pre-dual-write).
 *   3. Array of pointer dicts (post-dual-write).
 *
 * Every consumer in the frontend should funnel through these helpers so
 * we have one place to update if the shape changes again.
 */

import type {
  FormImagePointer,
  ImageUploadFieldValue,
} from "../types/formTemplates";

export function isFormImagePointer(value: unknown): value is FormImagePointer {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as FormImagePointer).s3_key === "string"
  );
}

export function isTombstonedPointer(value: unknown): boolean {
  if (!isFormImagePointer(value)) return false;
  const dt = value.deleted_at;
  return typeof dt === "string" && dt.length > 0;
}

export function readPointerS3Key(value: unknown): string | null {
  if (typeof value === "string") return value || null;
  if (isFormImagePointer(value)) return value.s3_key || null;
  return null;
}

export function readPointerDocumentUuid(value: unknown): string | null {
  if (!isFormImagePointer(value)) return null;
  return value.salon_document_uuid && value.salon_document_uuid.length > 0
    ? value.salon_document_uuid
    : null;
}

export function readPointerFilename(value: unknown): string | null {
  if (isFormImagePointer(value) && typeof value.filename === "string") {
    return value.filename;
  }
  const key = readPointerS3Key(value);
  if (!key) return null;
  // Form-upload S3 key basename layout is `{ts}-{filename}`. Strip the
  // millisecond timestamp prefix so the UI shows the original name.
  const basename = key.split("/").pop() ?? key;
  const dashIdx = basename.indexOf("-");
  if (dashIdx <= 0) return basename;
  const head = basename.slice(0, dashIdx);
  if (!/^[0-9]+$/.test(head)) return basename;
  return basename.slice(dashIdx + 1);
}

/**
 * Normalize any of the three on-disk shapes to an array. Strings are
 * wrapped to a single-element array; pointer dicts likewise. `null` /
 * `undefined` / anything unexpected becomes `[]`.
 */
export function coerceImageUploadField(
  value: ImageUploadFieldValue,
): Array<string | FormImagePointer> {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string" || isFormImagePointer(value)) return [value];
  return [];
}

/** Yield only non-tombstoned entries from an image_upload value. */
export function activeImageEntries(
  value: ImageUploadFieldValue,
): Array<string | FormImagePointer> {
  return coerceImageUploadField(value).filter((e) => !isTombstonedPointer(e));
}
